import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PaymentElement,
  useStripe,
  useElements,
  AddressElement
} from '@stripe/react-stripe-js';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Box,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Divider,
  Paper
} from '@mui/material';
import { ArrowLeft, AlertCircle } from 'lucide-react';

const PaymentForm = ({ reviewData, onBack }) => {
  const navigate = useNavigate();
  const stripe = useStripe();
  const elements = useElements();
  const { getAccessTokenSilently, user } = useAuth0();

  const [isProcessing, setIsProcessing] = useState(false);
  const [isElementsLoading, setIsElementsLoading] = useState(true);
  const [paymentError, setPaymentError] = useState(null);
  const [shippingAddress, setShippingAddress] = useState(null);

  // Get the correct eSIM status - check both legacy and new field names
  const isEsim = reviewData?.userDetails?.hasEsim || reviewData?.userDetails?.esim || false;

  // Check if ALL lines are eSIM
  const isAllLinesEsim = reviewData?.userDetails?.lineDetails && 
    reviewData.userDetails.lineDetails.length > 0 && 
    reviewData.userDetails.lineDetails.every(line => line.esim);

  // Determine if shipping address is needed - don't need shipping if all lines are eSIM
  const needsShippingAddress = !isAllLinesEsim && !isEsim;

  // Calculate the total amount due today based on the reviewData
  const calculateTotalAmount = () => {
    if (!reviewData) return 0;
    
    // Extract values, defaulting to 0 if undefined
    const monthlyRate = Number(reviewData.planDetails?.monthlyRate || 0);
    const taxes = Number(reviewData.planDetails?.taxes || 0);
    const activationFee = Number(reviewData.planDetails?.totalActivationFee || 0);
    const subscriptionCost = Number(reviewData.subscriptionDetails?.totalSubscriptionCost || 0);
    
    // Calculate shipping cost based on method
    const isExpedited = reviewData.subscriptionDetails?.shipping === 'expedited';
    const shippingCost = isEsim ? 0 : (isExpedited ? 15.00 : 8.00);
    
    // Calculate totals
    const verizonTotal = monthlyRate + taxes + activationFee;
    const circledinTotal = subscriptionCost + shippingCost;
    
    return verizonTotal + circledinTotal;
  };

  // Store the calculated total
  const totalDueToday = calculateTotalAmount();

  const validatePaymentDetails = useCallback(() => {
    if (!stripe || !elements) {
      setPaymentError('Payment system is not fully loaded. Please wait.');
      return false;
    }
  
    if (!totalDueToday) {
      setPaymentError('Invalid payment amount.');
      return false;
    }
  
    if (!reviewData?.subscriptionDetails?.type) {
      setPaymentError('Subscription type is required.');
      return false;
    }
  
    // Add circle validation
    if (!reviewData?.userDetails?.circle_id || !reviewData?.userDetails?.circle_name) {
      setPaymentError('Circle information is missing. Please try again.');
      console.error('Missing circle information:', {
        circle_id: reviewData?.userDetails?.circle_id,
        circle_name: reviewData?.userDetails?.circle_name
      });
      return false;
    }
  
    // Only validate shipping address if physical SIM is needed
    if (needsShippingAddress && !shippingAddress) {
      setPaymentError('Shipping address is required for physical SIM cards.');
      return false;
    }
  
    return true;
  }, [stripe, elements, reviewData, needsShippingAddress, shippingAddress, totalDueToday]);

  const handlePayment = async (e) => {
    e.preventDefault();
  
    if (!validatePaymentDetails()) {
      return;
    }
  
    try {
      setIsProcessing(true);
      setPaymentError(null);
  
      const { error: submitError } = await elements.submit();
      if (submitError) {
        throw new Error(submitError.message);
      }
  
      const token = await getAccessTokenSilently({
        audience: process.env.REACT_APP_AUTH0_AUDIENCE,
        scope: 'openid profile email'
      });
  
      // Format multiple IMEIs and eSIMs with comma delimiter
      let imeiString = '';
      let esimDigitalSimString = '';
      
      // Check if lineDetails exists and has entries
      if (reviewData.userDetails.lineDetails && reviewData.userDetails.lineDetails.length > 0) {
        // Join IMEIs with comma delimiter
        imeiString = reviewData.userDetails.lineDetails
          .map(line => line.imei || '')
          .filter(imei => imei) // Filter out empty IMEIs
          .join(',');
        
        // Join eSIM values with comma delimiter, but only for lines with eSIM enabled
        esimDigitalSimString = reviewData.userDetails.lineDetails
          .filter(line => line.esim)
          .map(line => line.imei2 || '')
          .filter(imei2 => imei2) // Filter out empty IMEI2s
          .join(',');
      } else {
        // Fallback to legacy single values if no lineDetails
        imeiString = reviewData.userDetails.imei || '';
        esimDigitalSimString = reviewData.userDetails.imei2 || '';
      }
  
      console.log('Review Data:', reviewData);
      console.log('Circle ID:', reviewData.userDetails.circle_id);
      console.log('Circle Name:', reviewData.userDetails.circle_name);
      console.log('IMEI String:', imeiString);
      console.log('eSIM Digital SIM String:', esimDigitalSimString);
      console.log('Is eSIM:', isEsim);
      console.log('Needs Shipping Address:', needsShippingAddress);
      console.log('Total Due Today:', totalDueToday);
  
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/payments/create-payment-intent/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          plan_id: reviewData.planDetails.planId,
          plan_name: reviewData.planDetails.planName,
          plan_type: reviewData.planDetails.planType || 'welcome',
          lines: reviewData.planDetails.lines || 1,
          subscription_type: reviewData.subscriptionDetails.type,
          shipping_method: reviewData.subscriptionDetails.shipping,
          is_esim: isEsim, // Use the consolidated eSIM status
          amount: Math.round(totalDueToday * 100), // Use the total amount due today instead of just monthly
          circle_id: reviewData.userDetails.circle_id,
          circle_name: reviewData.userDetails.circle_name,
          carrier: reviewData.planDetails.carrier || 'verizon',
          customer_email: user?.email,
          customer_name: user?.name || user?.email,
          joining_condition: reviewData.userDetails.joining_condition,
          
          
          // Updated Device Information with concatenated values
          imei: imeiString,
          esim_digital_sim: esimDigitalSimString,
          
          // Port Information
          port_number: reviewData.userDetails.type === "porting" || reviewData.userDetails.port_number,
          switch_contact_number: reviewData.userDetails.phoneNumbers?.join(',') || reviewData.userDetails.phoneNumber || '',
          // Circledin never collects or stores the carrier account number or transfer
          // PIN -- whoever performs the port gets it from the member at port time.
          previous_carrier: reviewData.userDetails.previousCarrier || '',
          previous_carrier_cost: reviewData.userDetails.previousBill ? parseFloat(reviewData.userDetails.previousBill) : null,
          
          // Location Information
          area_code: reviewData.userDetails.area_code || '',
          zip_code: reviewData.userDetails.zip_code || '',
        })
      });
  
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Payment intent creation failed');
      }
  
      const { clientSecret } = await response.json();
  
      // Default complete billing address
      const defaultBillingAddress = {
        line1: '',
        line2: '',  // Include empty line2
        city: '',
        state: '',
        postal_code: '00000',
        country: 'US'
      };
  
      // Create complete billing details
      const billingDetails = {
        name: user?.name || user?.email,
        email: user?.email,
        phone: shippingAddress?.phone || '',
        address: shippingAddress?.address || defaultBillingAddress
      };
  
      // If using shipping address, ensure it has all required fields
      if (shippingAddress?.address) {
        billingDetails.address = {
          ...defaultBillingAddress,  // Start with default fields
          ...shippingAddress.address,  // Override with actual values
          line2: shippingAddress.address.line2 || ''  // Ensure line2 exists
        };
      }
  
      const confirmOptions = {
        elements,
        clientSecret,
        confirmParams: {
          return_url: `${window.location.origin}/success`,
          payment_method_data: {
            billing_details: billingDetails,
            type: 'card'
          }
        }
      };
  
      // Add shipping details only if physical SIM is required
      if (needsShippingAddress && shippingAddress) {
        confirmOptions.confirmParams.shipping = {
          name: shippingAddress.name,
          phone: shippingAddress.phone,
          address: {
            ...defaultBillingAddress,  // Include all address fields
            ...shippingAddress.address,  // Override with actual values
            line2: shippingAddress.address.line2 || ''  // Ensure line2 exists
          }
        };
      }
  
      console.log('Confirming payment with options:', {
        billingDetails: confirmOptions.confirmParams.payment_method_data.billing_details,
        shipping: confirmOptions.confirmParams.shipping
      });
  
      const { error: confirmError } = await stripe.confirmPayment(confirmOptions);
  
      if (confirmError) {
        console.error('Payment confirmation error:', confirmError);
        throw new Error(confirmError.message);
      }
  
    } catch (err) {
      console.error('Payment processing error:', err);
      setPaymentError(err.message || 'An error occurred while processing your payment.');
    } finally {
      setIsProcessing(false);
    }
  };

  if (!reviewData) {
    return (
      <Alert
        severity="error"
        icon={<AlertCircle />}
      >
        Payment information is missing. Please try again.
      </Alert>
    );
  }

  return (
    <Paper elevation={0} className="payment-form" sx={{ p: 3 }}>
      {paymentError && (
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          icon={<AlertCircle />}
        >
          {paymentError}
        </Alert>
      )}

      {needsShippingAddress && (
        <>
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom fontWeight={600}>
              Shipping Address
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Please enter your shipping address for the physical SIM card.
            </Typography>
            <AddressElement
              options={{
                mode: 'shipping',
                allowedCountries: ['US'],
                fields: { phone: 'always' },
                validation: {
                  phone: {
                    required: 'always',
                  },
                },
                defaultValues: {
                  name: user?.name || user?.email,
                  address: {
                    country: 'US',
                  },
                },
              }}
              onChange={(event) => {
                if (event.complete) {
                  setShippingAddress(event.value);
                }
              }}
            />
          </Box>
          <Divider sx={{ my: 4 }} />
        </>
      )}

      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" gutterBottom fontWeight={600}>
          Payment Details
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Your payment includes a one-time charge for the carrier plan, activation fee
          {!isEsim && ', shipping,'} and your first CircledIn subscription payment.
        </Typography>
        <Box sx={{ position: 'relative', mt: 2 }}>
          {isElementsLoading && (
            <Box sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 1
            }}>
              <CircularProgress size={24} />
            </Box>
          )}
          <PaymentElement
            options={{
              layout: 'tabs',
              paymentMethodOrder: ['card'],
              fields: {
                billingDetails: 'never'
              },
              defaultValues: {
                billingDetails: {
                  address: {
                    country: 'US',
                  }
                }
              }
            }}
            onReady={() => setIsElementsLoading(false)}
            onChange={(event) => {
              if (event.error) {
                setPaymentError(event.error.message);
              } else {
                setPaymentError(null);
              }
            }}
          />
        </Box>
      </Box>

      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        gap: 2,
        mt: 4
      }}>
        <Button
          onClick={onBack}
          startIcon={<ArrowLeft />}
          variant="outlined"
          disabled={isProcessing}
          sx={{
            color: 'text.secondary',
            borderColor: 'divider',
            '&:hover': {
              borderColor: 'text.secondary'
            }
          }}
        >
          Back
        </Button>

        <Button
          onClick={handlePayment}
          variant="contained"
          disabled={isProcessing || (needsShippingAddress && !shippingAddress)}
          sx={{
            bgcolor: '#FF4775',
            color: 'white',
            '&:hover': {
              bgcolor: '#FF385C'
            },
            '&:disabled': {
              bgcolor: '#FCA5A5'
            }
          }}
        >
          {isProcessing ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={16} sx={{ color: 'white' }} />
              Processing...
            </Box>
          ) : (
            `Pay $${totalDueToday.toFixed(2)}`
          )}
        </Button>
      </Box>
    </Paper>
  );
};

export default PaymentForm;


